/**
 * Helper functions for date handling to avoid timezone issues
 * 
 * Problem: When a date like "2026-03-02" is stored and parsed with `new Date()`,
 * JavaScript interprets it as UTC midnight. When displayed in a local timezone
 * like Guatemala (UTC-6), it shows as the previous day.
 * 
 * Solution: Parse dates as local timezone or use consistent formatting.
 */

/**
 * Parses a date string and returns a Date object in local timezone
 * Handles both ISO strings (YYYY-MM-DD) and full ISO datetime strings
 * @param {string} dateString - The date string to parse
 * @returns {Date|null} - A Date object or null if invalid
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  
  // If it's just a date string (YYYY-MM-DD), parse as local
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }
  
  // If it's an ISO datetime string, extract the date part and parse as local
  if (dateString.includes('T')) {
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  
  // Fallback to regular Date parsing
  return new Date(dateString);
};

/**
 * Formats a date for display using local timezone
 * @param {string|Date} dateInput - The date to format
 * @param {string} locale - The locale for formatting (default: 'es-GT')
 * @returns {string} - Formatted date string or '-' if invalid
 */
export const formatLocalDate = (dateInput, locale = 'es-GT') => {
  if (!dateInput) return '-';
  
  const date = typeof dateInput === 'string' ? parseLocalDate(dateInput) : dateInput;
  
  if (!date || isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Checks if a date is in the past (overdue)
 * @param {string|Date} dateInput - The date to check
 * @returns {boolean} - True if the date is in the past
 */
export const isDatePast = (dateInput) => {
  if (!dateInput) return false;
  
  const date = typeof dateInput === 'string' ? parseLocalDate(dateInput) : dateInput;
  
  if (!date || isNaN(date.getTime())) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return date < today;
};

/**
 * Gets today's date as a YYYY-MM-DD string
 * @returns {string} - Today's date in YYYY-MM-DD format
 */
export const getTodayString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

export default {
  parseLocalDate,
  formatLocalDate,
  isDatePast,
  getTodayString
};
